import React, { useCallback, useEffect, useRef, useState } from "react";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import { toPng } from "html-to-image";
import { downloadPngDataUrl } from "../../admin/welcome/downloadPngDataUrl";
import slidingAppIcon from "../../assets/app-icons/sliding-puzzle-quest-app-icon.png";
import { JIGSAW_MOBILE_TITLE } from "../../prototype/mobile-jigsaw/routes";
import { SLIDING_PUZZLE_QUEST_TITLE } from "../../prototype/mobile-sliding/routes";
import { WORD_WHEEL_QUEST_TITLE } from "../../prototype/mobile-word-wheel/routes";
import SlidingAnimatedIcon, {
    slidingIconLoopMs,
    CTA_SLIDE_HOLD_MS,
    CTA_SLIDE_EXIT_MS,
} from "./SlidingAnimatedIcon";
import {
    downloadSlidingAdMobBannerGif,
    SLIDING_ADMOB_BANNER_GIF_FILE,
} from "./slidingAdMobBannerGif";

/** Google AdMob landscape — 1.91:1; 1200×628 common upload size. */
export const ADMOB_LANDSCAPE_W = 1200;
export const ADMOB_LANDSCAPE_H = 628;

/** Google AdMob square — 1:1. */
export const ADMOB_SQUARE_W = 1200;
export const ADMOB_SQUARE_H = 1200;

/** Google AdMob standard banner. */
export const ADMOB_BANNER_W = 320;
export const ADMOB_BANNER_H = 50;

/** Soft limit reminder (AdMob upload max 5120 KB). */
export const ADMOB_MAX_FILE_KB = 5120;

const TO_PNG_OPTS = {
    cacheBust: true,
    pixelRatio: 1,
    skipFonts: true,
};

const BRAND_STACK =
    '"Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif';

/** Rounded casual-game title stack for Word Wheel ad creatives. */
const GAME_TITLE_STACK =
    '"Trebuchet MS", "Segoe UI", "Helvetica Neue", Arial, sans-serif';

/** Elegant classic title stack for Jigsaw Muse ad creatives. */
const ELEGANT_TITLE_STACK =
    'Georgia, "Palatino Linotype", "Book Antiqua", "Times New Roman", serif';

const PUBLIC = process.env.PUBLIC_URL || "";

const PALETTE_PAINTS = ["#DC2626", "#7C3AED", "#2563EB", "#16A34A", "#EAB308", "#EA580C"];

function CrownIcon({ size = 12, color = "#FDE68A" }) {
    return (
        <Box
            component="svg"
            viewBox="0 0 24 24"
            sx={{ width: size, height: size, flexShrink: 0, display: "none" }}
            aria-hidden
        >
            <path
                d="M3 18h18l-1.2-9.5L15.5 12 12 4.5 8.5 12 4.2 8.5 3 18z"
                fill={color}
                stroke="#B45309"
                strokeWidth="1.2"
                strokeLinejoin="round"
            />
            <rect x="4" y="18" width="16" height="2.5" rx="1" fill={color} stroke="#B45309" strokeWidth="0.8" />
        </Box>
    );
}

function BannerSparkles() {
    const dots = [
        { left: "18%", top: "18%", s: 2, o: 0.9 },
        { left: "42%", top: "70%", s: 1.5, o: 0.7 },
        { left: "58%", top: "22%", s: 2.5, o: 0.85 },
        { left: "72%", top: "62%", s: 1.5, o: 0.65 },
        { left: "88%", top: "28%", s: 2, o: 0.8 },
        { left: "30%", top: "48%", s: 1.5, o: 0.55 },
    ];
    return (
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}>
            {dots.map((d, i) => (
                <Box
                    key={i}
                    sx={{
                        position: "absolute",
                        left: d.left,
                        top: d.top,
                        width: d.s,
                        height: d.s,
                        borderRadius: "50%",
                        bgcolor: `rgba(255,255,255,${d.o})`,
                        boxShadow: `0 0 ${d.s * 2}px rgba(186,230,253,0.9)`,
                    }}
                />
            ))}
        </Box>
    );
}

export const APP_ADMOB_CONFIG = {
    "jigsaw-muse": {
        title: JIGSAW_MOBILE_TITLE,
        tagline: "Piece beautiful puzzles",
        iconSrc: `${PUBLIC}/ad/jigsaw-muse-app-icon-512.png`,
        filePrefix: "jigsaw-muse-admob",
        gradient: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 40%, #BBF7D0 100%)",
        glow: "radial-gradient(ellipse 70% 55% at 50% 42%, rgba(255,255,255,0.8) 0%, transparent 70%)",
        titleColor: "#14532D",
        taglineColor: "#3F6212",
        bannerBg: "linear-gradient(90deg, #166534 0%, #15803D 55%, #4ADE80 100%)",
        bannerText: "#F0FDF4",
    },
    "word-wheel-quest": {
        title: WORD_WHEEL_QUEST_TITLE,
        tagline: "Spin letters. Find words.",
        iconSrc: `${PUBLIC}/ad/word-wheel-quest-app-icon-512.png`,
        filePrefix: "word-wheel-quest-admob",
        gradient: "linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 40%, #BAE6FD 100%)",
        glow: "radial-gradient(ellipse 70% 55% at 50% 42%, rgba(255,255,255,0.8) 0%, transparent 70%)",
        titleColor: "#0C4A6E",
        taglineColor: "#0369A1",
        bannerBg: "linear-gradient(90deg, #0C4A6E 0%, #0369A1 55%, #38BDF8 100%)",
        bannerText: "#F0F9FF",
    },
    "sliding-puzzle-quest": {
        title: SLIDING_PUZZLE_QUEST_TITLE,
        tagline: "Slide tiles. Solve the picture.",
        iconSrc: slidingAppIcon,
        filePrefix: "sliding-puzzle-quest-admob",
        gradient: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 40%, #A7F3D0 100%)",
        glow: "radial-gradient(ellipse 70% 55% at 50% 42%, rgba(255,255,255,0.8) 0%, transparent 70%)",
        titleColor: "#14532D",
        taglineColor: "#166534",
        bannerBg: "linear-gradient(90deg, #14532D 0%, #15803D 50%, #3BA666 100%)",
        bannerText: "#ECFDF5",
    },
};

function HeroCreative({
    width,
    height,
    config,
    logoScale,
    titleSize,
    subtitleSize,
    frameRef,
}) {
    const logoPx = Math.round(512 * logoScale);
    return (
        <Box
            ref={frameRef}
            sx={{
                width,
                height,
                position: "relative",
                overflow: "hidden",
                background: config.gradient,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
                px: Math.round(width * 0.06),
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: config.glow,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: Math.round(height * 0.035),
                }}
            >
                <Box
                    component="img"
                    src={config.iconSrc}
                    alt=""
                    sx={{
                        width: logoPx,
                        height: logoPx,
                        flexShrink: 0,
                        borderRadius: Math.round(logoPx * 0.18),
                        objectFit: "cover",
                        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.18)",
                        display: "block",
                    }}
                />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        component="div"
                        sx={{
                            fontFamily: BRAND_STACK,
                            fontWeight: 800,
                            fontSize: titleSize,
                            letterSpacing: "-0.03em",
                            color: config.titleColor,
                            lineHeight: 1.15,
                            whiteSpace: "nowrap",
                            textAlign: "center",
                        }}
                    >
                        {config.title}
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            mt: 0.75,
                            fontFamily: BRAND_STACK,
                            fontWeight: 600,
                            fontSize: subtitleSize,
                            color: config.taglineColor,
                            letterSpacing: "0.02em",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            textAlign: "center",
                        }}
                    >
                        {config.tagline}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

/** Compact 320×50 standard banner — icon + full title + Open CTA (Jigsaw etc.). */
function BannerCreative({ config, frameRef }) {
    const titleLen = (config.title || "").length;
    // Short names (e.g. "Jigsaw Muse") get a larger title.
    const titleSize = titleLen > 14 ? 14 : 17;

    return (
        <Box
            ref={frameRef}
            sx={{
                width: ADMOB_BANNER_W,
                height: ADMOB_BANNER_H,
                position: "relative",
                overflow: "hidden",
                background: config.bannerBg,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 6,
                boxSizing: "border-box",
                px: "7px",
                borderRadius: "10px",
            }}
        >
            <Box
                component="img"
                src={config.iconSrc}
                alt=""
                sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: "8px",
                    objectFit: "cover",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                    display: "block",
                }}
            />
            <Typography
                component="div"
                sx={{
                    fontFamily: GAME_TITLE_STACK,
                    fontWeight: 900,
                    fontSize: titleSize,
                    letterSpacing: "-0.03em",
                    color: config.bannerText,
                    lineHeight: 1.05,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    textAlign: "left",
                    textShadow: "0 1px 2px rgba(0,0,0,0.28)",
                }}
            >
                {config.title}
            </Typography>
            <Box sx={{ flex: "1 1 auto", minWidth: 4 }} />
            <Box
                sx={{
                    flexShrink: 0,
                    height: 28,
                    px: 1.25,
                    borderRadius: "14px",
                    bgcolor: "rgba(255,255,255,0.95)",
                    color: config.titleColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                }}
            >
                <Typography
                    component="div"
                    sx={{
                        fontFamily: BRAND_STACK,
                        fontWeight: 800,
                        fontSize: 12,
                        letterSpacing: "0.03em",
                        lineHeight: 1,
                        textTransform: "uppercase",
                    }}
                >
                    Open
                </Typography>
            </Box>
        </Box>
    );
}

/**
 * Sliding Puzzle Quest banner — SLIDE exits by sliding right; PLAY stays put.
 */
function SlidingBannerCreative({ frameRef }) {
    /** @type {['slide'|'exiting'|'play', Function]} */
    const [phase, setPhase] = useState("slide");
    const ctaW = 68;

    useEffect(() => {
        let cancelled = false;
        const timers = [];
        const later = (fn, ms) => {
            const t = window.setTimeout(() => {
                if (!cancelled) fn();
            }, ms);
            timers.push(t);
        };

        // Sequence: SLIDE shows → hold 1s → slide right slowly → then PLAY
        const loop = slidingIconLoopMs();
        const slideHoldMs = CTA_SLIDE_HOLD_MS;
        const exitMs = CTA_SLIDE_EXIT_MS; // slow slide-out
        const playHoldMs = Math.max(1400, loop - slideHoldMs - exitMs);

        const runCycle = () => {
            // 1. Show SLIDE only (PLAY hidden)
            setPhase("slide");
            later(() => {
                // 2. After 1s, whole SLIDE button slides right
                setPhase("exiting");
                later(() => {
                    // 3. Only after SLIDE is gone, show PLAY and stay
                    setPhase("play");
                    later(runCycle, playHoldMs);
                }, exitMs);
            }, slideHoldMs);
        };

        runCycle();
        return () => {
            cancelled = true;
            timers.forEach((t) => window.clearTimeout(t));
        };
    }, []);

    const showSlideLayer = phase === "slide" || phase === "exiting";
    const slideExiting = phase === "exiting";
    // PLAY only after SLIDE has fully exited — never behind SLIDE
    const showPlayLayer = phase === "play";

    return (
        <Box
            ref={frameRef}
            sx={{
                width: ADMOB_BANNER_W,
                height: ADMOB_BANNER_H,
                position: "relative",
                overflow: "hidden",
                borderRadius: "6px",
                boxSizing: "border-box",
                background: "#FFF7ED",
                border: "1.5px solid #E7E5E4",
                boxShadow: "0 2px 8px rgba(28,25,23,0.12)",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: "linear-gradient(180deg, #F97316 0%, #EA580C 50%, #C2410C 100%)",
                    zIndex: 0,
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    left: 8,
                    right: ctaW + 14,
                    top: 0,
                    bottom: 0,
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "7px",
                    minWidth: 0,
                    overflow: "hidden",
                }}
            >
                <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                    <SlidingAnimatedIcon size={38} />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        minWidth: 0,
                        overflow: "hidden",
                        gap: "1px",
                    }}
                >
                    <Typography
                        component="div"
                        sx={{
                            fontFamily: BRAND_STACK,
                            fontWeight: 800,
                            fontSize: 8,
                            letterSpacing: "0.16em",
                            color: "#EA580C",
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            textTransform: "uppercase",
                        }}
                    >
                        Slide · Solve
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            fontFamily: BRAND_STACK,
                            fontWeight: 800,
                            fontSize: 13,
                            letterSpacing: "-0.04em",
                            color: "#1C1917",
                            lineHeight: 1.05,
                            whiteSpace: "nowrap",
                        }}
                    >
                        Sliding Puzzle Quest
                    </Typography>
                </Box>
            </Box>

            {/* Glossy pills: whole SLIDE slides right; PLAY stays put */}
            <Box
                sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    marginTop: "-15px",
                    zIndex: 2,
                    width: ctaW,
                    height: 30,
                }}
            >
                {/* PLAY — glossy blue pill, never moves */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "999px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "3px",
                        background: "linear-gradient(180deg, #60A5FA 0%, #3B82F6 45%, #1D4ED8 100%)",
                        boxShadow: showPlayLayer
                            ? "0 2px 6px rgba(29,78,216,0.45), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(30,64,175,0.35)"
                            : "none",
                        border: "1px solid rgba(255,255,255,0.28)",
                        opacity: showPlayLayer ? 1 : 0,
                        transition: "opacity 240ms ease",
                        zIndex: 1,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            left: 4,
                            right: 4,
                            top: 2,
                            height: "42%",
                            borderRadius: "999px",
                            background:
                                "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%)",
                            pointerEvents: "none",
                        }}
                    />
                    <Box
                        component="svg"
                        viewBox="0 0 12 12"
                        sx={{ width: 10, height: 10, flexShrink: 0, position: "relative" }}
                        aria-hidden
                    >
                        <path d="M3.2 2.2v7.6L10 6z" fill="#FFFFFF" />
                    </Box>
                    <Typography
                        component="div"
                        sx={{
                            position: "relative",
                            fontFamily: BRAND_STACK,
                            fontWeight: 800,
                            fontSize: 10,
                            letterSpacing: "0.04em",
                            color: "#FFFFFF",
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            textShadow: "0 1px 1px rgba(30,64,175,0.45)",
                        }}
                    >
                        PLAY
                    </Typography>
                </Box>

                {/* SLIDE — glossy orange pill, whole chip slides right */}
                {showSlideLayer ? (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "999px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "3px",
                            background: "linear-gradient(180deg, #FB923C 0%, #F97316 45%, #C2410C 100%)",
                            boxShadow:
                                "0 2px 6px rgba(194,65,12,0.4), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(154,52,18,0.35)",
                            border: "1px solid rgba(255,255,255,0.28)",
                            transform: slideExiting ? "translateX(140%)" : "translateX(0)",
                            transition: slideExiting
                                ? "transform 1100ms cubic-bezier(0.22, 0.61, 0.36, 1)"
                                : "none",
                            zIndex: 2,
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                left: 4,
                                right: 4,
                                top: 2,
                                height: "42%",
                                borderRadius: "999px",
                                background:
                                    "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%)",
                                pointerEvents: "none",
                            }}
                        />
                        <Box
                            component="svg"
                            viewBox="0 0 12 12"
                            sx={{ width: 10, height: 10, flexShrink: 0, position: "relative" }}
                            aria-hidden
                        >
                            <path d="M3.2 2.2v7.6L10 6z" fill="#FFFFFF" />
                        </Box>
                        <Typography
                            component="div"
                            sx={{
                                position: "relative",
                                fontFamily: BRAND_STACK,
                                fontWeight: 800,
                                fontSize: 10,
                                letterSpacing: "0.04em",
                                color: "#FFFFFF",
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                textShadow: "0 1px 1px rgba(154,52,18,0.45)",
                            }}
                        >
                            SLIDE
                        </Typography>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
}

function WordWheelBannerCreative({ config, frameRef }) {
    return (
        <Box
            ref={frameRef}
            sx={{
                width: ADMOB_BANNER_W,
                height: ADMOB_BANNER_H,
                position: "relative",
                overflow: "hidden",
                borderRadius: "12px",
                boxSizing: "border-box",
                background:
                    "linear-gradient(105deg, #082F49 0%, #0C4A6E 28%, #0E7490 62%, #22D3EE 100%)",
                boxShadow:
                    "0 6px 16px rgba(8, 47, 73, 0.35), inset 0 1px 0 rgba(255,255,255,0.22)",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                px: "7px",
                gap: "6px",
            }}
        >
            {/* Geometric pattern overlay */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.18,
                    backgroundImage: `
                        linear-gradient(135deg, rgba(255,255,255,0.12) 25%, transparent 25%),
                        linear-gradient(225deg, rgba(255,255,255,0.08) 25%, transparent 25%),
                        linear-gradient(45deg, rgba(255,255,255,0.06) 25%, transparent 25%),
                        linear-gradient(315deg, rgba(14,116,144,0.35) 25%, transparent 25%)
                    `,
                    backgroundSize: "14px 14px",
                    backgroundPosition: "0 0, 7px 0, 7px -7px, 0 7px",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />
            {/* Soft light bloom */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(ellipse 55% 120% at 18% 50%, rgba(125,211,252,0.35) 0%, transparent 55%), radial-gradient(ellipse 40% 100% at 88% 50%, rgba(34,211,238,0.28) 0%, transparent 60%)",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />
            <BannerSparkles />

            {/* App icon with glowing border */}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: "9px",
                    p: "1.5px",
                    background:
                        "linear-gradient(145deg, #F0F9FF 0%, #7DD3FC 45%, #38BDF8 100%)",
                    boxShadow:
                        "0 0 10px rgba(56,189,248,0.75), 0 2px 6px rgba(8,47,73,0.45)",
                }}
            >
                <Box
                    component="img"
                    src={config.iconSrc}
                    alt=""
                    sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "7.5px",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            </Box>

            {/* Title + crown */}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "4px",
                    minWidth: 0,
                    flex: "1 1 auto",
                }}
            >
                <CrownIcon size={14} />
                <Typography
                    component="div"
                    sx={{
                        fontFamily: GAME_TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 16.5,
                        letterSpacing: "-0.03em",
                        color: "#FFFFFF",
                        lineHeight: 1.05,
                        whiteSpace: "nowrap",
                        textShadow: `
                            -1.25px -1.25px 0 #082F49,
                            1.25px -1.25px 0 #082F49,
                            -1.25px 1.25px 0 #082F49,
                            1.25px 1.25px 0 #082F49,
                            0 2px 3px rgba(0,0,0,0.45)
                        `,
                    }}
                >
                    Word Wheel Quest
                </Typography>
            </Box>

            {/* Glossy PLAY NOW CTA */}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flexShrink: 0,
                    height: 30,
                    pl: "4px",
                    pr: "9px",
                    borderRadius: "15px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1px",
                    background:
                        "linear-gradient(180deg, #86EFAC 0%, #22C55E 42%, #16A34A 100%)",
                    boxShadow:
                        "0 0 12px rgba(34,197,94,0.65), 0 3px 0 #15803D, 0 4px 8px rgba(0,0,0,0.28)",
                    border: "1px solid rgba(255,255,255,0.35)",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: 0,
                        height: "48%",
                        background:
                            "linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 100%)",
                        pointerEvents: "none",
                    }}
                />
                <PlayArrowRoundedIcon
                    sx={{
                        fontSize: 18,
                        color: "#FFFFFF",
                        filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.35))",
                        position: "relative",
                    }}
                />
                <Typography
                    component="div"
                    sx={{
                        position: "relative",
                        fontFamily: GAME_TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 9.5,
                        letterSpacing: "0.04em",
                        color: "#FFFFFF",
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        textShadow: "0 1px 2px rgba(21,128,61,0.75)",
                    }}
                >
                    PLAY NOW
                </Typography>
            </Box>
        </Box>
    );
}

/** Ornate dark-teal + gold logo plaque. */
function JigsawLogoPlaque({ iconSrc }) {
    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 1,
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: "40% 40% 38% 38% / 42% 42% 36% 36%",
                p: "2px",
                background:
                    "linear-gradient(145deg, #F8E7A0 0%, #D4AF37 35%, #8B6914 70%, #F6D060 100%)",
                boxShadow:
                    "0 0 12px rgba(212,175,55,0.65), 0 2px 5px rgba(0,0,0,0.5)",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "inherit",
                    overflow: "hidden",
                    background:
                        "radial-gradient(circle at 35% 30%, #1A6B6B 0%, #0F4C4C 45%, #063636 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "inset 0 1px 2px rgba(255,255,255,0.15)",
                }}
            >
                <Box
                    component="img"
                    src={iconSrc}
                    alt=""
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        objectFit: "cover",
                        display: "block",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                    }}
                />
            </Box>
        </Box>
    );
}

/** Artist palette with spectrum paints + brush tip. */
function MiniArtistPalette() {
    return (
        <Box
            sx={{
                position: "relative",
                width: 72,
                height: 44,
                flexShrink: 0,
                zIndex: 1,
            }}
        >
            <Box
                component="svg"
                viewBox="0 0 44 28"
                sx={{ width: 72, height: 44, display: "none", display: "block" }}
                aria-hidden
            >
                <ellipse cx="20" cy="15" rx="18" ry="11" fill="#8B5A2B" />
                <ellipse cx="20" cy="14" rx="16" ry="9.5" fill="#C4A484" />
                <circle cx="32" cy="14" r="4.5" fill="#A67C52" />
                {PALETTE_PAINTS.map((c, i) => (
                    <circle
                        key={c}
                        cx={8 + i * 4.2}
                        cy={11 + (i % 2) * 5}
                        r="2.4"
                        fill={c}
                        stroke="rgba(0,0,0,0.25)"
                        strokeWidth="0.4"
                    />
                ))}
                <rect x="34" y="3" width="2" height="16" rx="1" fill="#5C4033" transform="rotate(25 35 11)" />
                <circle cx="39" cy="5" r="2" fill="#DC2626" />
            </Box>
        </Box>
    );
}

/**
 * Jigsaw Muse — ornate gallery-atelier 320×50 banner
 * (museum wall, gold-framed Venice jigsaw, elegant title, palette, PLAY NOW).
 */
function JigsawMuseBannerCreative({ config, frameRef }) {
    return (
        <Box
            ref={frameRef}
            sx={{
                width: ADMOB_BANNER_W,
                height: ADMOB_BANNER_H,
                position: "relative",
                overflow: "hidden",
                borderRadius: "10px",
                boxSizing: "border-box",
                background:
                    "linear-gradient(105deg, #1A120B 0%, #2C1810 22%, #3D2914 48%, #2A1F18 72%, #1A120B 100%)",
                boxShadow:
                    "0 6px 16px rgba(26,18,11,0.4), inset 0 1px 0 rgba(255,230,180,0.12)",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                px: "6px",
                gap: "5px",
            }}
        >
            {/* Gallery wall depth + floor hint */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 40%, transparent 62%, rgba(0,0,0,0.45) 100%),
                        radial-gradient(ellipse 40% 90% at 22% 0%, rgba(255,220,150,0.22) 0%, transparent 70%),
                        radial-gradient(ellipse 35% 80% at 78% 0%, rgba(255,210,140,0.18) 0%, transparent 65%)
                    `,
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />
            {/* Spotlight cones */}
            <Box
                sx={{
                    position: "absolute",
                    left: "12%",
                    top: 0,
                    width: 40,
                    height: "100%",
                    background:
                        "linear-gradient(180deg, rgba(255,236,179,0.28) 0%, rgba(255,220,150,0.06) 55%, transparent 100%)",
                    clipPath: "polygon(35% 0, 65% 0, 100% 100%, 0 100%)",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: "48%",
                    top: 0,
                    width: 48,
                    height: "100%",
                    background:
                        "linear-gradient(180deg, rgba(255,236,179,0.22) 0%, rgba(255,220,150,0.05) 50%, transparent 100%)",
                    clipPath: "polygon(38% 0, 62% 0, 100% 100%, 0 100%)",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />
            {/* Warm bloom / magical glow */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(ellipse 50% 80% at 40% 55%, rgba(212,175,55,0.18) 0%, transparent 60%)",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />
            <BannerSparkles />

            <JigsawLogoPlaque iconSrc={config.iconSrc} />

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    minWidth: 0,
                    flex: "1 1 auto",
                    gap: "6px",
                }}
            >
                <Typography
                    component="div"
                    sx={{
                        fontFamily: ELEGANT_TITLE_STACK,
                        fontWeight: 700,
                        fontSize: 15,
                        fontStyle: "italic",
                        letterSpacing: "0.01em",
                        color: "#FFF8E7",
                        lineHeight: 1.05,
                        whiteSpace: "nowrap",
                        textShadow: `
                            -1px -1px 0 #3D2914,
                            1px -1px 0 #3D2914,
                            -1px 1px 0 #3D2914,
                            1px 1px 0 #3D2914,
                            0 0 8px rgba(212,175,55,0.55),
                            0 2px 3px rgba(0,0,0,0.5)
                        `,
                    }}
                >
                    Jigsaw Muse
                </Typography>
                <MiniArtistPalette />
            </Box>

            {/* Glossy gold-teal PLAY NOW */}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flexShrink: 0,
                    height: 30,
                    pl: "3px",
                    pr: "8px",
                    borderRadius: "15px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "0px",
                    background:
                        "linear-gradient(180deg, #F6E7A0 0%, #D4AF37 40%, #A67C00 100%)",
                    boxShadow:
                        "0 0 12px rgba(212,175,55,0.7), 0 3px 0 #6B4F12, 0 4px 8px rgba(0,0,0,0.35)",
                    border: "1px solid rgba(255,248,220,0.45)",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: 0,
                        height: "48%",
                        background:
                            "linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)",
                        pointerEvents: "none",
                    }}
                />
                <PlayArrowRoundedIcon
                    sx={{
                        fontSize: 17,
                        color: "#2C1810",
                        filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.35))",
                        position: "relative",
                    }}
                />
                <Typography
                    component="div"
                    sx={{
                        position: "relative",
                        fontFamily: ELEGANT_TITLE_STACK,
                        fontWeight: 700,
                        fontSize: 9,
                        letterSpacing: "0.04em",
                        color: "#2C1810",
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        textShadow: "0 1px 0 rgba(255,248,220,0.45)",
                    }}
                >
                    PLAY NOW
                </Typography>
            </Box>
        </Box>
    );
}

function FormatCard({
    title,
    subtitle,
    width,
    height,
    fileName,
    previewMaxW,
    savingKey,
    saving,
    saveError,
    onDownload,
    children,
    previewPad = 0,
    downloadLabel = "Download PNG",
    captionExtra = null,
}) {
    const frameRef = useRef(null);
    const previewScale = Math.min(1, previewMaxW / width);
    const previewH = Math.round(height * previewScale) + previewPad * 2;
    const previewW = Math.round(width * previewScale) + previewPad * 2;
    const busy = saving === savingKey;

    return (
        <Box
            sx={{
                flex: "1 1 320px",
                maxWidth: 560,
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: 2,
                boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {subtitle}
            </Typography>
            <Box
                sx={{
                    width: previewW,
                    height: previewH,
                    mx: "auto",
                    mb: 1.5,
                    position: "relative",
                    overflow: previewPad > 0 ? "visible" : "hidden",
                    borderRadius: 1,
                    boxShadow: previewPad > 0 ? "none" : "0 6px 24px rgba(15,23,42,0.12)",
                    bgcolor: "transparent",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        width: Math.round(width * previewScale),
                        height: Math.round(height * previewScale),
                        position: "relative",
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width,
                            height,
                            transform: `scale(${previewScale})`,
                            transformOrigin: "top left",
                        }}
                    >
                        {children(frameRef)}
                    </Box>
                </Box>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        busy ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />
                    }
                    disabled={saving != null}
                    onClick={() => onDownload(frameRef, fileName, savingKey)}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#0ea5e9",
                        "&:hover": { bgcolor: "#0284c7" },
                    }}
                >
                    {downloadLabel}
                </Button>
            </Stack>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
            >
                {width}×{height} · {fileName} · max {ADMOB_MAX_FILE_KB} KB
                {captionExtra ? ` · ${captionExtra}` : ""}
            </Typography>
            {saveError && savingKey === saveError.key ? (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", textAlign: "center", mt: 0.5 }}
                >
                    {saveError.message}
                </Typography>
            ) : null}
        </Box>
    );
}

/**
 * Per-app Google AdMob creatives — landscape, square, and 320×50 banner.
 * @param {{ appKey: keyof typeof APP_ADMOB_CONFIG }} props
 */
export default function AppGoogleAdMobPage({ appKey }) {
    const config = APP_ADMOB_CONFIG[appKey];
    const [saving, setSaving] = useState(null);
    const [saveError, setSaveError] = useState(null);

    const handleDownload = useCallback(
        async (frameRef, fileName, savingKey) => {
            if (saving != null) return;
            setSaving(savingKey);
            setSaveError(null);
            try {
                if (savingKey === "banner-gif") {
                    await downloadSlidingAdMobBannerGif(fileName || SLIDING_ADMOB_BANNER_GIF_FILE);
                    return;
                }
                const node = frameRef.current;
                if (!node) return;
                const dataUrl = await toPng(node, {
                    ...TO_PNG_OPTS,
                    width: node.offsetWidth,
                    height: node.offsetHeight,
                    backgroundColor: "#FFFFFF",
                });
                await downloadPngDataUrl(dataUrl, fileName);
            } catch (e) {
                setSaveError({
                    key: savingKey,
                    message: e?.message || "Failed to download",
                });
            } finally {
                setSaving(null);
            }
        },
        [saving],
    );

    if (!config) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">Unknown AdMob app: {String(appKey)}</Typography>
            </Box>
        );
    }

    const landscapeFile = `${config.filePrefix}-landscape-1200x628.png`;
    const squareFile = `${config.filePrefix}-square-1200x1200.png`;
    const bannerFile = `${config.filePrefix}-banner-320x50.png`;

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#F1F5F9",
                py: { xs: 2, sm: 3 },
                px: { xs: 1.5, sm: 3 },
                boxSizing: "border-box",
            }}
        >
            <Box sx={{ maxWidth: 1100, mx: "auto" }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Google AdMob — {config.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Upload assets for AdMob image creatives. Landscape 1.91:1 (&gt;600×314), square
                    1:1 (≥300×300), and standard banner 320×50. File size limit {ADMOB_MAX_FILE_KB}{" "}
                    KB each.
                </Typography>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2.5}
                    useFlexGap
                    flexWrap="wrap"
                    justifyContent="center"
                >
                    <FormatCard
                        title="Landscape image"
                        subtitle="Ratio 1.91:1 · greater than 600×314"
                        width={ADMOB_LANDSCAPE_W}
                        height={ADMOB_LANDSCAPE_H}
                        fileName={landscapeFile}
                        previewMaxW={480}
                        savingKey="landscape"
                        saving={saving}
                        saveError={saveError}
                        onDownload={handleDownload}
                    >
                        {(frameRef) => (
                            <HeroCreative
                                width={ADMOB_LANDSCAPE_W}
                                height={ADMOB_LANDSCAPE_H}
                                config={config}
                                logoScale={0.38}
                                titleSize={appKey === "sliding-puzzle-quest" ? 40 : 48}
                                subtitleSize={20}
                                frameRef={frameRef}
                            />
                        )}
                    </FormatCard>
                    <FormatCard
                        title="Square image"
                        subtitle="Ratio 1:1 · at least 300×300"
                        width={ADMOB_SQUARE_W}
                        height={ADMOB_SQUARE_H}
                        fileName={squareFile}
                        previewMaxW={360}
                        savingKey="square"
                        saving={saving}
                        saveError={saveError}
                        onDownload={handleDownload}
                    >
                        {(frameRef) => (
                            <HeroCreative
                                width={ADMOB_SQUARE_W}
                                height={ADMOB_SQUARE_H}
                                config={config}
                                logoScale={0.5}
                                titleSize={appKey === "sliding-puzzle-quest" ? 48 : 56}
                                subtitleSize={24}
                                frameRef={frameRef}
                            />
                        )}
                    </FormatCard>
                    <FormatCard
                        title="Standard banner"
                        subtitle={
                            appKey === "word-wheel-quest"
                                ? "320×50 · sleek game ad · PLAY NOW"
                                : appKey === "jigsaw-muse"
                                  ? "320×50 · gallery atelier · PLAY NOW"
                                  : appKey === "sliding-puzzle-quest"
                                    ? "320×50 · Google Ads GIF · smooth tile slides · SLIDE/PLAY"
                                    : "320×50 · AdMob banner slot"
                        }
                        width={ADMOB_BANNER_W}
                        height={ADMOB_BANNER_H}
                        fileName={
                            appKey === "sliding-puzzle-quest"
                                ? SLIDING_ADMOB_BANNER_GIF_FILE
                                : bannerFile
                        }
                        previewMaxW={480}
                        savingKey={appKey === "sliding-puzzle-quest" ? "banner-gif" : "banner"}
                        saving={saving}
                        saveError={saveError}
                        onDownload={handleDownload}
                        downloadLabel={
                            appKey === "sliding-puzzle-quest" ? "Download GIF" : "Download PNG"
                        }
                        captionExtra={
                            appKey === "sliding-puzzle-quest"
                                ? "tiles slide · SLIDE CTA slides off · then PLAY"
                                : null
                        }
                        previewPad={
                            appKey === "word-wheel-quest"
                            || appKey === "jigsaw-muse"
                            || appKey === "sliding-puzzle-quest"
                                ? 16
                                : 0
                        }
                    >
                        {(frameRef) =>
                            appKey === "word-wheel-quest" ? (
                                <WordWheelBannerCreative config={config} frameRef={frameRef} />
                            ) : appKey === "jigsaw-muse" ? (
                                <JigsawMuseBannerCreative config={config} frameRef={frameRef} />
                            ) : appKey === "sliding-puzzle-quest" ? (
                                <SlidingBannerCreative frameRef={frameRef} />
                            ) : (
                                <BannerCreative config={config} frameRef={frameRef} />
                            )
                        }
                    </FormatCard>
                </Stack>
            </Box>
        </Box>
    );
}
